import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import type { ReactNode } from 'react';
import { ALERT, BUTTON_PRIMARY, BUTTON_SECONDARY, LABEL } from './ui';
import { CloseButton } from './close-button';

/**
 * The dialog every migrated screen opens: a slate header bar, a body that
 * scrolls on its own, and one row of actions at the foot.
 *
 * The kanban and enquiry dialogs are hand-rolled copies of this markup; this
 * is the same thing, named, for everything written since.
 *
 * What goes inside is `shared/ui` — the fields, the buttons and the banner are
 * the app's, not the dialog's. Only the frame is defined here.
 */

export { FIELD_CLASS } from './ui';

export function Field({
  label,
  required = false,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className={`${LABEL} mb-1 block`}>
        {label}
        {required && <span className="text-hilevel-red"> *</span>}
      </span>
      {children}
    </label>
  );
}

/** Error banner shown at the top of a dialog body. */
export function DialogError({ message }: { message: string | null }) {
  if (!message) return null;
  return <p className={`${ALERT}`}>{message}</p>;
}

/** The footer: cancel on the left of the action that matters. */
export function DialogActions({
  onCancel,
  onConfirm,
  confirmLabel,
  busyLabel,
  busy = false,
  disabled = false,
  cancelLabel = 'Cancel',
  extra,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  busyLabel: string;
  busy?: boolean;
  disabled?: boolean;
  cancelLabel?: string;
  /** A third action — a delete, sitting away from the other two. */
  extra?: ReactNode;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-hilevel-border bg-hilevel-light/50 px-4 py-3 sm:px-6">
      {extra && <div className="mr-auto">{extra}</div>}
      <button type="button" className={BUTTON_SECONDARY} onClick={onCancel}>
        {cancelLabel}
      </button>
      <button
        type="button"
        className={BUTTON_PRIMARY}
        disabled={disabled || busy}
        onClick={onConfirm}
      >
        {busy ? busyLabel : confirmLabel}
      </button>
    </div>
  );
}

export function DialogShell({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean;
  title: string;
  /** A line under the title — what the dialog is about, not what it does. */
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  width?: string;
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />
      <div className="fixed inset-0 flex items-center justify-center sm:p-4">
        {/* Below `sm` the dialog is the screen: no inset, no corners, no
            border, and `max-sm:max-w-none` to override the `width` the caller
            asked for — 32px of padding and a rounded edge are worth having on
            a desktop, and are room a phone does not have to give. The override
            is a media-query variant so it wins over the plain `max-w-*` it is
            cancelling whatever order the two land in. From `sm` up every one
            of these is the value it was. */}
        <DialogPanel
          className={`flex h-full max-h-[100dvh] w-full flex-col overflow-hidden border-hilevel-border bg-white shadow-lg max-sm:max-w-none sm:h-auto sm:max-h-[90vh] sm:rounded sm:border ${width}`}
        >
          <div className="flex shrink-0 items-center gap-3 bg-hilevel-slate px-4 py-4 sm:px-6">
            <div className="min-w-0">
              <h4 className="text-lg font-semibold text-white">{title}</h4>
              {subtitle && <p className="mt-0.5 truncate text-xs text-white/70">{subtitle}</p>}
            </div>
            <CloseButton onClick={onClose} variant="dark" />
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6">{children}</div>

          {footer}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
